import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { IP } from "../../../../internalServer";
import ZoomableImage from "../../../../Components/zoomablePic";
import CrossAttachmentAssessment from "../../Create_Assessment/Attachment/CrossAttachmentAssessment";
import PdfViewer from "../../../../Components/QuestionEditCreate/PdfViewer";

/* ───────── Helper: safe attachment renderer ───────── */
const AttachmentBlock = ({ attachment, onPdfClick }) => {
  if (!attachment || typeof attachment !== "string") return null;

  const ext = attachment.split(".").pop()?.toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  const isPdf = ext === "pdf";

  const fullUrl = attachment.startsWith("http")
    ? attachment
    : `${IP}${attachment}`;

  return (
    <Box maxWidth={220}>
      {isImage ? (
        <ZoomableImage src={fullUrl} />
      ) : isPdf ? (
        <CrossAttachmentAssessment
          attachment={attachment}
          onClick={() => onPdfClick?.(attachment)}
        />
      ) : (
        <CrossAttachmentAssessment attachment={attachment} />
      )}
    </Box>
  );
};

/* ───────── Helper: attachment grid ───────── */
const AttachmentGrid = ({ list, onPdfClick }) => {
  if (!Array.isArray(list) || list.length === 0) return null;

  return (
    <Box sx={{ my: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
      {list.map((att, index) => (
        <AttachmentBlock key={index} attachment={att} onPdfClick={onPdfClick} />
      ))}
    </Box>
  );
};

const McqResultCard = ({
  data,
  isCorrect,
  label,
  isDescriptive,
  value,
  teacherComment,
  obtained_marks,
}) => {
  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");

  if (!data) return null;

  const question = data.question || {};
  const userAnswer = data?.answer;
  const userAnswerKey = data?.answer?.key;
  const correctAnswerKey = question?.correct_answers?.[0]?.option_key;
  const correctAnswerObj = question?.correct_answers?.[0];
  const reasonForAnswer = correctAnswerObj?.reason_for_answer;
  const expectedExplanation = reasonForAnswer?.explanation ?? null;
  const expectedAttachment = reasonForAnswer?.attachment ?? null;

  const handleClickforpdf = (fileUrl) => {
    setResponseAttach(fileUrl);
    setMideaviewer(true);
  };

  return (
    <Box sx={{ p: 2, border: "1px solid #ddd", borderRadius: 2, mb: 3 }}>
      {/* ───────── Question title + marks ───────── */}
      <Typography
        variant="h6"
        className="d-flex justify-content-between items-center"
        gutterBottom
      >
        <div>
          {label}. {question?.title || "Question"}
        </div>
        <div>
          (
          {!isDescriptive ? (
            <span>
              <span>Marks - </span>
              <span
                style={{
                  fontWeight: "bold",
                  color: obtained_marks === 0 ? "red" : "green",
                }}
              >
                {obtained_marks ?? 0}
              </span>
              /
              <span style={{ fontWeight: "bold", color: "black" }}>
                {question?.correct_score ?? 0}
              </span>
            </span>
          ) : (
            <span>
              <span>Marks - </span>
              <span
                style={{
                  color:
                    obtained_marks === 0
                      ? "red"
                      : obtained_marks === question?.correct_score
                        ? "green"
                        : "orange",
                  fontWeight: "bold",
                }}
              >
                {obtained_marks ?? 0}
              </span>
              /
              <span style={{ color: "green", fontWeight: "bold" }}>
                {question?.correct_score ?? 0}
              </span>
            </span>
          )}
          )
        </div>
      </Typography>

      {/* ───────── Question attachments ───────── */}
      <AttachmentGrid
        list={question?.attachments}
        onPdfClick={handleClickforpdf}
      />

      {/* ───────── Descriptive ───────── */}
      {isDescriptive ? (
        <Box sx={{ mt: 2 }}>
          {/* Your Answer */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: "#fafafa",
              border: "2px solid",
              borderColor:
                value === 0
                  ? "red"
                  : value === question?.correct_score
                    ? "green"
                    : "orange",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                mb: 1,
                color:
                  value === 0
                    ? "red"
                    : value === question?.correct_score
                      ? "green"
                      : "orange",
              }}
            >
              Your Answer:
            </Typography>

            <Typography sx={{ mb: 1 }}>
              {data?.description ||
                data?.answer?.explanation ||
                "No answer provided"}
            </Typography>

            {/* User uploaded attachments */}
            {Array.isArray(data?.attachments) &&
              data.attachments.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {data.attachments.map((att, idx) => {
                    if (!att) return null;
                    const url = att.startsWith("http") ? att : IP + att;
                    return (
                      <img
                        key={idx}
                        src={url}
                        alt={`user-answer-attachment-${idx}`}
                        style={{
                          maxWidth: "150px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
          </Box>

          {/* Teacher comment */}
          {teacherComment && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#fffbe6",
                border: "1px dashed #f0ad4e",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontStyle: "italic",
                  color: "#8a6d3b",
                }}
              >
                Teacher's Comment
              </Typography>
              <Typography sx={{ color: "#5a4632" }}>
                {teacherComment}
              </Typography>
            </Box>
          )}

          {/* Expected Answer */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#e8f5e9",
              border: "1px solid #81c784",
            }}
          >
            <Typography sx={{ fontWeight: "bold", mb: 1, color: "#2e7d32" }}>
              Expected Answer:
            </Typography>
            <Typography sx={{ mb: 1 }}>
              {expectedExplanation || "No correct answer explanation"}
            </Typography>

            {expectedAttachment && (
              <AttachmentBlock
                attachment={expectedAttachment}
                onPdfClick={handleClickforpdf}
              />
            )}
          </Box>
        </Box>
      ) : (
        /* ───────── MCQ ───────── */
        <>
          {Array.isArray(question?.options) &&
            question.options.map((opt) => {
              const isCorrectOpt = opt?.key === correctAnswerKey;
              const isUser = opt?.key === userAnswerKey;

              return (
                <Box
                  key={opt?.key}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    bgcolor: isCorrectOpt
                      ? "#e6f4ea"
                      : isUser
                        ? "#fdecea"
                        : "#fff",
                    border: isCorrectOpt
                      ? "1px solid #4caf50"
                      : isUser
                        ? "1px solid #f44336"
                        : "1px solid #ddd",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", mr: 1 }}>
                    {opt?.key}.
                  </Typography>
                  <Typography sx={{ flexGrow: 1 }}>{opt?.value}</Typography>

                  {isCorrectOpt && (
                    <Typography sx={{ color: "#4caf50", fontWeight: "bold" }}>
                      Correct Answer
                    </Typography>
                  )}
                  {isUser && !isCorrectOpt && (
                    <Typography sx={{ color: "#f44336", fontWeight: "bold" }}>
                      Your Answer
                    </Typography>
                  )}

                  {opt?.attachment && (
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        ml: 4,
                      }}
                    >
                      <img
                        src={IP + opt.attachment}
                        alt={`option-${opt?.key}-attachment`}
                        style={{
                          maxWidth: "70px",
                          borderRadius: "6px",
                          border: "1px solid #ccc",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}

          {/* Result */}
          <Typography
            sx={{
              mt: 2,
              color: isCorrect ? "green" : "red",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
            }}
          >
            {userAnswer?.key === " " ? (
              <div className="d-flex align-items-center">
                <img src="/HwImg/WrongAnswerIcon.png" alt="" className="ml-2" />
                <div style={{ color: "red" }}>✘ Student Not Answered</div>
              </div>
            ) : userAnswer?.key === correctAnswerKey ? (
              <div className="mt-3">
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center">
                    <img src="/HwImg/CoorectAnswerIcon.png" alt="" />
                  </div>
                  <div style={{ color: "green" }}>✔ Correct</div>
                </div>
              </div>
            ) : (
              <div className="mt-3">
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex align-items-center">
                    <img src="/HwImg/WrongAnswerIcon.png" alt="" />
                  </div>
                  <div style={{ color: "red" }}>✘ Wrong</div>
                </div>
              </div>
            )}
          </Typography>

          {/* Explanation */}
          <Typography sx={{ mt: 1, color: "gray" }}>
            {expectedExplanation || data?.answer?.explanation || ""}
          </Typography>

          {/* Teacher comment */}
          {teacherComment && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#fffbe6",
                border: "1px dashed #f0ad4e",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontStyle: "italic",
                  color: "#8a6d3b",
                }}
              >
                Teacher's Comment
              </Typography>
              <Typography sx={{ color: "#5a4632" }}>
                {teacherComment}
              </Typography>
            </Box>
          )}
        </>
      )}

      {mideaviewer && (
        <PdfViewer
          setMideaviewer={setMideaviewer}
          attachment={responseAttach}
        />
      )}
    </Box>
  );
};

export default McqResultCard;
